import React, { FC, useRef, useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { hasPermission } from 'utils/permissions';
import { t } from 'i18next';
import { useAuthContext } from 'contexts/AuthContext';
import clsx from 'clsx';
import * as LucideIcons from 'lucide-react';
import GlobalTooltip from './GlobalTooltip';
import ThemedSvg from './ThemedSvg';

/**
 * Fixed-pixel Lucide renderer for the rail.
 *
 * The shared `LucideIcon` wrapper multiplies every size by `--unit-minthr`
 * (a `vw`-based unit), so on wide screens its icons render noticeably larger
 * than the kit's `NavRail`, which uses fixed px. The rail must match the kit
 * 1:1, so we render raw `lucide-react` at fixed px here instead.
 */
const RailIcon: FC<{
  name?: string | React.ElementType;
  size: number;
  className?: string;
  strokeWidth?: number;
}> = ({ name, size, className, strokeWidth = 1.8 }) => {
  if (!name) return null;
  const Cmp = (
    typeof name === 'string'
      ? (LucideIcons as Record<string, React.ElementType>)[name]
      : name
  ) as React.ElementType | undefined;
  if (!Cmp) return null;
  return <Cmp size={size} strokeWidth={strokeWidth} className={className} />;
};

const FLYOUT_EDGE = 8;

const fitFlyoutToViewport = (trigger: HTMLElement) => {
  const panel = trigger.querySelector<HTMLElement>(':scope > [data-flyout]');
  if (!panel) return;
  const triggerTop = trigger.getBoundingClientRect().top;
  const top = Math.max(
    FLYOUT_EDGE,
    Math.min(triggerTop, window.innerHeight - FLYOUT_EDGE - panel.offsetHeight),
  );
  panel.style.setProperty('--flyout-top', `${top - triggerTop}px`);
};

interface SidenavProps {
  isSidenavOpen: boolean;
  setIsSidenavOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
interface NavItem {
  label: string;
  link?: string;
  hasPermission: string[] | boolean;
  icon?: string | React.ElementType;
  subItems?: NavItem[];
  subIcon?: string;
}

const navItems: NavItem[] = [
  {
    label: 'SideBar.Dashbord',
    link: '/',
    icon: 'LayoutDashboard',
    hasPermission: true,
  },
  {
    label: 'mainSideBar.profile',
    icon: 'User',
    hasPermission: true,
    subItems: [
      {
        label: 'Sidebar.InfosPersos',
        link: '/employee/{userId}/profile/public',
        hasPermission: true,
      },
      {
        label: 'SideBar.myHoliday',
        link: '/employee/{userId}/conge/calendar',
        hasPermission: ['Conge.View'],
      },
      {
        label: 'SideBar.MyDocument',
        link: '/employee/{userId}/document/mydemand',
        hasPermission: ['DemandDocument.Profil.View.HimSelf'],
      },
      {
        label: 'SideBar.Material',
        link: '/employee/{userId}/material/hardware',
        hasPermission: ['Hardware.View'],
      },
      {
        label: 'SideBar.Note',
        link: '/employee/{userId}/profilenotes',
        hasPermission: ['ProfileNote.View'],
      },
      {
        label: 'SideBar.Training',
        link: '/employee/{userId}/trainings/profile',
        hasPermission: ['Training.View'],
      },
      {
        label: 'SideBar.Forms',
        link: '/employee/{userId}/forms/myforms',
        hasPermission: ['ProfileForm.View'],
      },
    ],
  },
  {
    label: 'SideBar.Calander',
    icon: 'Calendar',
    hasPermission: ['Conge.View'],
    subItems: [
      {
        label: 'SideBar.calender',
        link: '/conge/timeline',
        hasPermission: true,
      },
      {
        label: 'SideBar.conge.demandes',
        link: '/conge/demande',
        hasPermission: true,
      },
    ],
  },
  {
    label: 'SideBar.Collaborateur',
    link: '/collaborateur',
    icon: 'Users',
    hasPermission: true,
  },
  {
    // Links the legacy layout to the Performance module (kit routes). Shows if the user can read ANY
    // flipped Performance area — same ANY-of perms + href as the kit nav (navItems.tsx 'performance').
    label: 'SideBar.Performance',
    link: '/performance',
    icon: 'Target',
    hasPermission: [
      'Performance.Objectives.View',
      'Performance.Skills.View',
      'Performance.Feedback.Access',
    ],
  },
  {
    label: 'SideBar.documents',
    icon: 'FileText',
    hasPermission: ['CompanyDocument.View', 'DemandDocument.View'],
    subItems: [
      {
        label: 'SideBar.Demandes',
        link: '/document',
        hasPermission: ['DemandDocument.View'],
      },
      {
        label: 'SideBar.Document',
        link: '/companyfile',
        hasPermission: ['CompanyDocument.View'],
      },
      {
        label: 'SideBar.affectation',
        link: '/documentdistribution',
        hasPermission: ['DemandDocument.Manage.Distribution'],
      },
    ],
  },
  {
    label: 'SideBar.Surveys',
    link: '/surveys',
    icon: 'BarChart3',
    hasPermission: ['Surveys.View'],
  },
  {
    label: 'SideBar.inventaire',
    icon: 'Package',
    hasPermission: ['Hardware.View', 'Software.View', 'Intervention.View'],
    subItems: [
      {
        label: 'SideBar.Material',
        link: '/material/hardware',
        hasPermission: ['Hardware.View'],
      },
      {
        label: 'SideBar.software',
        link: '/material/software',
        hasPermission: ['Software.View'],
      },
      {
        label: 'SideBar.intervention',
        link: '/material/intervention',
        hasPermission: ['Intervention.View'],
      },
    ],
  },
  {
    label: 'Profile.navbar.ExpensesReport',
    link: '/expenses',
    icon: 'CreditCard',
    hasPermission: ['Expenses.View'],
  },
  {
    label: 'SideBar.Shift',
    icon: 'Clock',
    hasPermission: [
      'ShiftUser.View',
      'TimeCard.View',
      'Shift.View',
      'Shift.ManageReplaceRequest',
      'Shift.ManageChangeRequest',
      'Shift.Manage.View',
    ],
    subItems: [
      {
        label: 'MyShift',
        link: '/shift/my',
        hasPermission: ['ShiftUser.View'],
      },
      {
        label: 'SideBar.ShiftTimeline',
        link: '/shift',
        hasPermission: ['Shift.View'],
      },
      {
        label: 'SideBar.ShiftRequests',
        link: '/shiftdemands',
        hasPermission: [
          'Shift.ManageReplaceRequest',
          'Shift.ManageChangeRequest',
          'Shift.Manage.View',
        ],
      },
    ],
  },
  {
    label: 'Training.Title',
    link: '/trainings',
    icon: 'GraduationCap',
    hasPermission: ['Training.View'],
  },
  {
    label: 'SideBar.Annonces',
    link: '/annonces',
    icon: 'Megaphone',
    hasPermission: ['Annonce.View'],
  },
  {
    label: 'SideBar.Job',
    link: '/job',
    icon: 'Briefcase',
    hasPermission: ['Job.View'],
  },
  {
    label: 'Profile.navbar.Reporting',
    link: '/reporting',
    icon: 'BarChart2',
    hasPermission: ['Reporting.View'],
  },
  {
    label: 'Profile.navbar.ExportationsDynamic',
    link: '/exportdata',
    icon: 'Download',
    hasPermission: ['ExportationsDynamic.View'],
  },
  {
    label: 'Profile.navbar.Form',
    link: '/forms',
    icon: 'ClipboardList',
    hasPermission: ['Form.View'],
  },
  {
    label: 'Integration.navbar.Attendances',
    link: '/ams/attendances',
    icon: 'Fingerprint',
    hasPermission: ['Ams.Daily.View'],
  },
  {
    label: 'Profile.navbar.Evp',
    link: '/evp',
    icon: 'DollarSign',
    hasPermission: ['Evp.Payment.View'],
  },
  {
    label: 'Project.navbar.Project',
    link: '/project',
    icon: 'Folder',
    hasPermission: ['ProjectManage.View'],
  },
  {
    label: 'SideBar.Settings',
    link: '/settings/info',
    icon: 'Settings',
    hasPermission: ['Settings.View'],
  },
];

/**
 * Mobile rail item — hand-rolled to reproduce the kit NavRail's *expanded* item
 * styling 1:1 (icon tile + label + start-bar active indicator + accordion
 * children with a brand-300 connector line). The kit component itself is NOT
 * used; only its class recipe is copied so the mobile drawer matches the kit
 * rail. Uses next/link for soft nav + closes the drawer via `onNavigate`.
 */
const MobileNavItem: FC<{
  item: NavItem;
  isPathActive: (link?: string) => boolean;
  checkItemPermission: (item: NavItem) => boolean;
  interpolate: (link?: string) => string | undefined;
  onNavigate: () => void;
}> = ({ item, isPathActive, checkItemPermission, interpolate, onNavigate }) => {
  const children = item.subItems?.filter(checkItemPermission) ?? [];
  const hasChildren = children.length > 0;
  const childActive = children.some((c) => isPathActive(c.link));
  const isActive = isPathActive(item.link) || childActive;
  const [expanded, setExpanded] = useState<boolean>(childActive);

  const focusClass =
    'focus-visible:tw-outline-none focus-visible:tw-ring-[2px] focus-visible:tw-ring-brand-50 focus-visible:tw-ring-offset-[1px] tw-rounded-[8px] tw-block';

  const inner = (
    <span
      className={clsx(
        'tw-group tw-relative tw-flex tw-items-center tw-h-[36px] tw-mx-[6px] tw-rounded-[8px] tw-px-[8px] tw-transition-colors tw-duration-100 tw-cursor-pointer',
        isActive
          ? 'tw-bg-brand-50 tw-text-brand-500'
          : 'tw-text-gray-700 hover:tw-bg-brand-50 hover:tw-text-brand-500',
      )}
    >
      {isActive && (
        <span
          aria-hidden="true"
          className="tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-start-0 tw-w-[2px] tw-h-[20px] tw-bg-brand-500 tw-rounded-e-[2px]"
        />
      )}
      <span
        className={clsx(
          'tw-shrink-0 tw-inline-flex tw-items-center tw-justify-center tw-w-[20px] tw-h-[20px] tw-rounded-[6px] tw-transition-colors tw-duration-100',
          isActive
            ? 'tw-text-brand-500'
            : 'tw-text-gray-600 group-hover:tw-text-brand-500',
        )}
      >
        <RailIcon name={item.icon} size={20} strokeWidth={1.8} />
      </span>
      <span
        className={clsx(
          'tw-ms-[10px] tw-text-[13px] tw-truncate tw-flex-1 tw-text-start',
          isActive ? 'tw-font-medium' : 'tw-font-[400]',
        )}
      >
        {t(item.label)}
      </span>
      {hasChildren && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          aria-label={expanded ? 'Collapse section' : 'Expand section'}
          aria-expanded={expanded}
          className="tw-shrink-0 tw-ms-[4px] tw-inline-flex tw-items-center tw-justify-center tw-w-[24px] tw-h-[24px] tw-rounded-md tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-900 focus-visible:tw-outline-none focus-visible:tw-ring-[2px] focus-visible:tw-ring-brand-50"
        >
          <RailIcon
            name={expanded ? 'ChevronDown' : 'ChevronRight'}
            size={14}
            strokeWidth={1.8}
            className={expanded ? undefined : 'rtl:-tw-scale-x-100'}
          />
        </button>
      )}
    </span>
  );

  return (
    <div>
      {hasChildren && !item.link ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-current={isActive ? 'page' : undefined}
          className={clsx(
            focusClass,
            'tw-w-full tw-text-start tw-bg-transparent tw-border-0 tw-p-0',
          )}
        >
          {inner}
        </button>
      ) : (
        <Link
          href={interpolate(item.link) || '#'}
          onClick={onNavigate}
          aria-current={isActive ? 'page' : undefined}
          className={focusClass}
        >
          {inner}
        </Link>
      )}

      {hasChildren && expanded && (
        <ul
          role="group"
          aria-label={t(item.label)}
          className="tw-relative tw-list-none tw-m-0 tw-ps-[34px] tw-pe-[6px] tw-mt-[2px] tw-flex tw-flex-col tw-gap-[2px]"
        >
          <span
            aria-hidden="true"
            className="tw-absolute tw-top-[2px] tw-bottom-[2px] tw-start-[22px] tw-w-px tw-bg-brand-300"
          />
          {children.map((child) => {
            const active = isPathActive(child.link);
            return (
              <li key={child.link || child.label} className="tw-list-none">
                <Link
                  href={interpolate(child.link) || '#'}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  className={focusClass}
                >
                  <span
                    className={clsx(
                      'tw-relative tw-flex tw-items-center tw-h-[32px] tw-rounded-[6px] tw-ps-[10px] tw-pe-[10px] tw-transition-colors tw-duration-100 tw-cursor-pointer',
                      active
                        ? 'tw-bg-brand-50 tw-text-brand-500 tw-font-medium'
                        : 'tw-text-gray-700 hover:tw-bg-brand-50 hover:tw-text-brand-500',
                    )}
                  >
                    <span className="tw-text-[12px] tw-truncate tw-flex-1 tw-text-start">
                      {t(child.label)}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const Sidenav: FC<SidenavProps> = ({ isSidenavOpen, setIsSidenavOpen }) => {
  const sidenavRef = useRef<HTMLDivElement>(null);
  const dashboardItemRef = useRef<HTMLLIElement>(null);
  const [showMore, setShowMore] = useState<boolean>(false);
  const currentPath = usePathname();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { user } = useAuthContext();
  const [activeLabel, setActiveLabel] = useState<string>();
  const [visibleItemsCount, setVisibleItemsCount] = useState<number>(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const minthrMobileMediaQuery = window.matchMedia('(max-width: 480px)');

    setIsMobile(minthrMobileMediaQuery.matches);

    const handleResize = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    minthrMobileMediaQuery.addEventListener('change', handleResize);
    return () =>
      minthrMobileMediaQuery.removeEventListener('change', handleResize);
  }, []);

  // Close the mobile drawer with Escape (the backdrop handles click-to-close).
  // We deliberately do NOT use a document click-outside listener: it closed the
  // drawer when a nav accordion toggled, because the toggle re-render swapped
  // the clicked icon node out of the DOM, so `contains(target)` returned false.
  // The kit's AppShell drawer closes via a backdrop element for the same reason.
  useEffect(() => {
    if (!isSidenavOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsSidenavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isSidenavOpen, setIsSidenavOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const timer = setTimeout(() => {
      const calculateVisibleItems = () => {
        if (!sidenavRef.current || !dashboardItemRef.current) return;

        const sidenavHeight = sidenavRef.current.clientHeight;
        const dashboardItemHeight = dashboardItemRef.current.clientHeight;

        if (sidenavHeight > 0 && dashboardItemHeight > 0) {
          // Space taken by the brand box (desktop) or mobile logo header.
          const headerHeight = isMobile ? 80 : 44;
          // ul vertical padding (8+8) + the settings divider (~8).
          const padding = 24;
          const itemHeight = dashboardItemHeight;

          // Reserve two rows for the always-present dashboard + settings items.
          const availableHeight =
            sidenavHeight - headerHeight - padding - itemHeight * 2;

          const count = Math.max(Math.floor(availableHeight / itemHeight), 1);

          // Reserve one slot for the "More" overflow trigger. When everything
          // fits this still leaves count-1 ≥ middle-item count, so all items
          // render and no trigger appears — matching the kit's NavRail.
          setVisibleItemsCount(count - 1);
        }
      };

      calculateVisibleItems();
      window.addEventListener('resize', calculateVisibleItems);

      return (): void => {
        window.removeEventListener('resize', calculateVisibleItems);
      };
    }, 100);

    return (): void => clearTimeout(timer);
  }, [isMobile, isSidenavOpen]);

  useEffect(() => {
    if (isMobile) {
      setIsSidenavOpen(false);
    }
  }, [currentPath]);

  const isPathActive = (path?: string) => {
    if (!path) return false;
    const normalizedPath = path.replace(
      '{userId}',
      user.userId?.toString() || '',
    );
    if (normalizedPath === '/') return currentPath === '/';

    if (normalizedPath.includes('/employee/')) {
      const parts = normalizedPath.split('/');
      const currentParts = currentPath.split('/');
      return currentParts[1] === parts[1] && currentParts[3] === parts[3];
    }

    const targetPath =
      normalizedPath === '/settings/info' ? '/settings' : normalizedPath;
    return (
      currentPath === targetPath || currentPath.startsWith(targetPath + '/')
    );
  };

  const CheckIsActiveLink = (item: NavItem): boolean => {
    if (item.subItems) {
      return item.subItems.some((subItem) => isPathActive(subItem.link));
    }
    return isPathActive(item.link);
  };

  const checkItemPermission = (item: NavItem): boolean => {
    // Support-only entries: audit logs are visible exclusively to SupportMintHR.
    if (item.link === '/settings/auditlogs' && user.role !== 'SupportMintHR') {
      return false;
    }
    if (typeof item.hasPermission === 'boolean') return item.hasPermission;
    return item.hasPermission.some((p) => hasPermission(p));
  };

  const renderSubMenu = (
    items: NavItem[] | undefined,
    isActive: boolean,
    parentLabel: string,
    parentIcon?: string | React.ElementType,
  ) => (
    <div
      data-flyout
      className={clsx(
        'tw-absolute tw-top-[var(--flyout-top,0px)] tw-z-[200] tw-bg-white tw-shadow-[0_4px_12px_rgba(0,0,0,0.08),0_2px_4px_rgba(0,0,0,0.04)] tw-rounded-[8px] tw-transition-[opacity,visibility,transform] tw-duration-300 tw-ease-out tw-min-w-[180px] tw-border-[0.5px] tw-border-gray-200 tw-overflow-hidden',
        'ltr:tw-left-[calc(100%_+_8px)] rtl:tw-right-[calc(100%_+_8px)]',
        'mobile:tw-static mobile:tw-bg-transparent mobile:tw-shadow-none mobile:tw-px-0 mobile:tw-py-0 mobile:tw-border-none mobile:tw-overflow-visible',
        isMobile
          ? activeLabel === parentLabel || isActive
            ? 'tw-block tw-opacity-100'
            : 'tw-hidden tw-opacity-0'
          : 'tw-opacity-0 tw-invisible ltr:tw-translate-x-[-12px] rtl:tw-translate-x-[12px] group-hover/nav:tw-opacity-100 group-hover/nav:tw-visible group-hover/nav:tw-translate-x-0',
      )}
    >
      <div className="tw-px-[12px] tw-py-[10px] tw-bg-white tw-flex tw-flex-col tw-rounded-t-[8px] tw-border-b-[0.5px] tw-border-b-gray-100 mobile:tw-hidden">
        <span className="tw-font-medium !tw-text-[13px] tw-leading-tight tw-flex tw-items-center tw-gap-[8px] tw-text-gray-600">
          {parentIcon && (
            <RailIcon
              name={parentIcon}
              size={20}
              strokeWidth={1.8}
              className="tw-shrink-0 tw-text-gray-400"
            />
          )}
          {t(parentLabel)}
        </span>
      </div>
      <ul
        className={clsx(
          'tw-list-none tw-m-0 tw-flex tw-flex-col tw-p-[4px] tw-gap-[2px]',
          'mobile:ltr:tw-border-l mobile:ltr:tw-border-l-primary mobile:rtl:tw-border-r mobile:rtl:tw-border-r-primary mobile:tw-ml-4 mobile:tw-mt-1',
          isMobile
            ? activeLabel === parentLabel || isActive
              ? 'tw-block'
              : 'tw-hidden'
            : '',
        )}
      >
        {items?.filter(checkItemPermission).map((item, key) => {
          const itemPath =
            item.link?.replace('{userId}', user.userId?.toString() || '') ||
            '#';
          const isSubActive = isPathActive(item.link);

          return (
            <li key={item.link}>
              <Link
                href={itemPath}
                className={clsx(
                  'tw-flex tw-items-center tw-gap-[8px] tw-h-[32px] tw-px-[8px] tw-no-underline tw-text-[13px] tw-transition-colors tw-duration-100 tw-whitespace-nowrap tw-rounded-[4px] tw-w-full group',
                  isSubActive
                    ? 'tw-bg-brand-50 tw-text-brand-500 tw-font-medium'
                    : 'tw-text-gray-700 hover:tw-bg-brand-50 hover:tw-text-brand-500',
                  'mobile:tw-px-8 mobile:hover:tw-bg-transparent mobile:hover:tw-text-dark mobile:tw-border-none mobile:tw-py-2.5',
                )}
              >
                <RailIcon
                  name="ChevronRight"
                  size={11}
                  strokeWidth={1.8}
                  className="tw-flex-shrink-0 tw-text-gray-400 rtl:-tw-scale-x-100"
                />
                {t(item.label)}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );

  const renderNavItem = (
    item: NavItem,
    isStickyBottom?: boolean,
    ref?: React.RefObject<HTMLLIElement>,
  ) => {
    const isActive = CheckIsActiveLink(item);

    return (
      <li
        ref={ref}
        key={item.link || item.label}
        className={clsx(
          'tw-relative mobile:tw-p-0 mobile:tw-w-full mobile:tw-mb-2 tw-group/nav',
          isStickyBottom && hasPermission('Settings.View') ? 'tw-mb-0' : '',
        )}
        onMouseEnter={(e) => {
          if (isMobile || !item.subItems) return;
          setActiveLabel(item.label);
          fitFlyoutToViewport(e.currentTarget);
        }}
        onMouseLeave={() =>
          !isMobile && item.subItems && setActiveLabel(undefined)
        }
      >
        <Link
          href={item.link || '#'}
          className={clsx(
            'tw-block mobile:tw-flex mobile:tw-items-center tw-no-underline mobile:tw-w-full',
            // isMobile &&
            //   item.subItems &&
            //   "after:tw-content-[''] after:tw-w-[calc(var(--unit-minthr)*13)] after:tw-h-[calc(var(--unit-minthr)*13)] after:tw-bg-[url('/icons/arrow2.svg')] after:tw-bg-contain after:tw-bg-center after:tw-bg-no-repeat after:tw-absolute after:tw-top-1/2 after:-tw-translate-y-1/2 ltr:after:tw-right-[calc(var(--unit-minthr)*2)] rtl:after:tw-left-[calc(var(--unit-minthr)*2)] after:tw-brightness-[1.92] after:tw-grayscale",
          )}
          onClick={(e) => {
            if ((isMobile && item.subItems) || !item.link) {
              e.preventDefault();
              setActiveLabel(
                activeLabel === item.label ? undefined : item.label,
              );
            }
          }}
        >
          {item.icon && (
            <span
              className={clsx(
                'tw-flex tw-items-center tw-justify-center tw-rounded-[8px] tw-relative tw-transition-colors tw-duration-100 tw-h-[36px] tw-mx-[6px] tw-px-[12px] mobile:tw-w-[calc(var(--unit-minthr)*45)] mobile:tw-h-[calc(var(--unit-minthr)*45)] mobile:tw-mx-0 mobile:tw-px-0',
                isActive
                  ? 'tw-bg-brand-50 tw-text-brand-500'
                  : 'tw-text-gray-600 hover:tw-bg-brand-50 hover:tw-text-brand-500',
                isMobile && 'after:tw-hidden',
              )}
            >
              {isActive && !isMobile && (
                <span
                  aria-hidden="true"
                  className="tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-start-0 tw-w-[2px] tw-h-[20px] tw-bg-brand-500 tw-rounded-e-[2px]"
                />
              )}
              {/* Kit NavItemInner wraps the icon in a fixed 20px shrink-0 box
                 so the tile's px-[12px] padding can't squeeze the glyph
                 (flexbox would otherwise shrink it below its nominal size,
                 which also thins the stroke). Mirrors the kit 1:1. */}
              <span className="tw-shrink-0 tw-inline-flex tw-items-center tw-justify-center tw-w-[20px] tw-h-[20px]">
                <RailIcon
                  name={item.icon}
                  size={18}
                  strokeWidth={1.8}
                  className="tw-transition-colors tw-duration-100"
                />
              </span>
            </span>
          )}
          <span
            className={clsx(
              'tw-whitespace-nowrap',
              !isMobile && [
                // Kit-style Tooltip (matches @minthr-saas/ui-kit <Tooltip> dark variant)
                'tw-pointer-events-none tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-z-[90]',
                'tw-bg-gray-800 tw-text-white tw-rounded-[6px] tw-shadow-[0_2px_8px_rgba(0,0,0,0.12)]',
                'tw-px-[10px] tw-py-[6px] tw-text-[12px] tw-font-normal tw-leading-[1.4]',
                'ltr:tw-left-[calc(100%_+_8px)] rtl:tw-right-[calc(100%_+_8px)]',
                'tw-opacity-0 tw-invisible tw-transition-all tw-duration-150 tw-ease-out',
                'ltr:tw-translate-x-[-8px] rtl:tw-translate-x-[8px]',
                'group-hover/nav:tw-opacity-100 group-hover/nav:tw-visible group-hover/nav:tw-translate-x-0',
                item.subItems &&
                  'group-hover/nav:!tw-invisible group-hover/nav:!tw-opacity-0',
              ],
              isMobile && [
                'tw-ml-4 tw-text-dark tw-text-3 tw-font-medium tw-leading-[calc(var(--unit-minthr)*19)] tw-no-underline tw-block tw-opacity-100 tw-static tw-bg-transparent tw-shadow-none tw-p-0',
              ],
            )}
          >
            {!isMobile && (
              <span
                aria-hidden="true"
                className="tw-absolute tw-top-1/2 ltr:tw-left-0 rtl:tw-right-0 -tw-translate-y-1/2 ltr:-tw-translate-x-1/2 rtl:tw-translate-x-1/2 tw-rotate-45 tw-bg-gray-800 tw-w-[6px] tw-h-[6px]"
              />
            )}
            {t(item.label)}
          </span>
        </Link>

        {item.subItems &&
          renderSubMenu(
            item.subItems,
            isActive || activeLabel === item.label,
            item.label,
            item.icon,
          )}
      </li>
    );
  };

  const itemsWithPermission = navItems.filter(checkItemPermission);
  const dashboardItem = itemsWithPermission[0];
  const settingItem = itemsWithPermission[itemsWithPermission.length - 1];
  const middleItems = itemsWithPermission.slice(1, -1);
  const visibleSlice = middleItems.slice(0, visibleItemsCount);
  const overflowSlice = middleItems.slice(visibleItemsCount);
  // The "More" (···) trigger occupies a rail slot itself, so collapsing a
  // single item into it saves no space. Only fold items into the overflow
  // when there are at least two; when one (or none) would spill over, render
  // it directly so every item stays visible and no trigger appears.
  const hasRealOverflow = overflowSlice.length > 1;
  const visibleItems = hasRealOverflow ? visibleSlice : middleItems;
  const overflowItems = hasRealOverflow ? overflowSlice : [];

  // ── Mobile rail: the items are hand-rolled (via <MobileNavItem>) to match the
  //    kit NavRail's expanded styling 1:1 — NO kit component is imported. The
  //    Settings item sticks to the bottom like the kit's bottom section.
  const interpolate = (link?: string): string | undefined =>
    link ? link.replace('{userId}', user.userId?.toString() || '') : undefined;
  const railSettings = itemsWithPermission.find(
    (i) => i.link === '/settings/info',
  );
  const railMainItems = itemsWithPermission.filter(
    (i) => i.link !== '/settings/info',
  );

  return (
    <>
      {/* Mobile drawer backdrop — click to close. Kit-style scrim (see the
         AppShell mobile drawer): a separate element, NOT a document
         click-outside listener, so tapping items/accordions inside the drawer
         never closes it. Only visible at the mobile breakpoint while open. */}
      {isSidenavOpen && (
        <div
          className="tw-hidden mobile:tw-block tw-fixed tw-inset-0 tw-bg-black/40 tw-z-[290]"
          onClick={() => setIsSidenavOpen(false)}
          aria-hidden="true"
        />
      )}
      <div
        className={clsx(
          'tw-fixed tw-z-[49] tw-flex tw-flex-col tw-bg-gray-50 mobile:tw-bg-white tw-transition-transform tw-duration-200 tw-ease-in-out',
          'tw-start-0 tw-w-[52px] tw-top-0 [.minthr-logged-in_&]:tw-top-6 tw-bottom-0 tw-border-e-[0.5px] tw-border-gray-200',
          'mobile:tw-top-0 [.minthr-logged-in_&]:mobile:tw-top-0 mobile:tw-w-[280px] mobile:tw-z-[300] mobile:tw-border-e-0 mobile:tw-shadow-[0_4px_16px_rgba(0,0,0,0.12)]',
          isSidenavOpen
            ? 'mobile:tw-translate-x-0'
            : 'ltr:mobile:-tw-translate-x-full rtl:mobile:tw-translate-x-full',
        )}
        ref={sidenavRef}
      >
        {/* Desktop brand box — the gray-50 corner above the rail that the kit's
         AppShell renders in its "brand" grid area. Height matches the header
         (53px) so the nav items below line up with the header's bottom edge.
         Hidden on mobile (the off-canvas drawer has its own logo header). */}
        <Link
          href="/"
          className="tw-flex mobile:tw-hidden tw-shrink-0 tw-h-[44px] tw-items-center tw-justify-center"
        >
          <ThemedSvg
            src="/brand/minthr-mark.svg"
            aspect="137 / 137"
            alt="MintHR"
            className="tw-h-[24px] tw-w-auto"
          />
        </Link>

        <div className="tw-hidden mobile:tw-flex tw-items-center tw-justify-between tw-px-[16px] tw-pt-[16px] tw-pb-[12px] tw-border-b tw-border-b-[rgba(216,216,216,0.7)]">
          <Link className="tw-block" href="/">
            <ThemedSvg
              src="/icons/minthR-logo.svg"
              aspect="75 / 18"
              alt={'Logo Image'}
              className="tw-h-[20px] tw-w-auto"
            />
          </Link>
          <GlobalTooltip id="sidenav-close" title={t('Close')}>
            <button
              className="tw-bg-transparent tw-border-none tw-p-0 tw-leading-none"
              onClick={() => setIsSidenavOpen(false)}
              type="button"
            >
              <Image
                src="/icons/close.svg"
                alt={'Close icon'}
                width={20}
                height={20}
                className="tw-w-[20px] tw-h-[20px]"
              />
            </button>
          </GlobalTooltip>
        </div>

        {isMobile ? (
          <nav
            aria-label={t('Menu')}
            className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-py-[8px] tw-w-full"
          >
            <div className="tw-flex-1 tw-overflow-y-auto">
              <div className="tw-flex tw-flex-col tw-gap-[1px]">
                {railMainItems.map((item) => (
                  <MobileNavItem
                    key={item.link || item.label}
                    item={item}
                    isPathActive={isPathActive}
                    checkItemPermission={checkItemPermission}
                    interpolate={interpolate}
                    onNavigate={() => setIsSidenavOpen(false)}
                  />
                ))}
              </div>
            </div>
            {railSettings && (
              <div className="tw-shrink-0">
                <hr className="tw-mx-[12px] tw-my-[4px] tw-border-0 tw-border-t-[0.5px] tw-border-gray-100" />
                <div className="tw-flex tw-flex-col tw-gap-[1px]">
                  <MobileNavItem
                    key={railSettings.link || railSettings.label}
                    item={railSettings}
                    isPathActive={isPathActive}
                    checkItemPermission={checkItemPermission}
                    interpolate={interpolate}
                    onNavigate={() => setIsSidenavOpen(false)}
                  />
                </div>
              </div>
            )}
          </nav>
        ) : (
          <ul className="tw-flex-1 tw-min-h-0 tw-list-none tw-flex tw-flex-col tw-m-0 tw-px-0 tw-py-[8px] tw-gap-[1px] tw-w-full">
            {' '}
            {renderNavItem(dashboardItem, false, dashboardItemRef)}
            {visibleItems.map((item) => renderNavItem(item))}
            {overflowItems.length > 0 && (
              <ul className="tw-flex tw-w-full tw-h-fit tw-m-0 tw-p-0 tw-list-none tw-flex-col">
                <li
                  className="tw-relative tw-w-full tw-mb-0 tw-mt-auto tw-group"
                  onMouseEnter={(e) => fitFlyoutToViewport(e.currentTarget)}
                >
                  <a
                    onClick={() => setShowMore(!showMore)}
                    className="tw-flex tw-items-center tw-justify-center tw-h-[36px] tw-mx-[6px] tw-px-[12px] tw-rounded-[8px] tw-transition-colors tw-duration-100 tw-text-gray-600 hover:tw-bg-brand-50 hover:tw-text-brand-500 tw-cursor-pointer"
                  >
                    {/* Same fixed 20px shrink-0 box the other rail items use so
                       flexbox can't squeeze/thin the glyph (see renderNavItem). */}
                    <span className="tw-shrink-0 tw-inline-flex tw-items-center tw-justify-center tw-w-[20px] tw-h-[20px]">
                      <RailIcon
                        name="MoreHorizontal"
                        size={18}
                        strokeWidth={1.8}
                        className="tw-transition-colors tw-duration-100"
                      />
                    </span>
                  </a>
                  {/* Transparent hover-bridge spanning the gap so moving the
                     cursor from the trigger to the flyout doesn't drop hover. */}
                  <span
                    aria-hidden="true"
                    className="tw-hidden group-hover:tw-block tw-absolute tw-top-0 tw-bottom-0 ltr:tw-left-full rtl:tw-right-full tw-w-[10px] tw-z-[200]"
                  />
                  <ul
                    data-flyout
                    className={clsx(
                      'tw-flex tw-invisible tw-absolute tw-top-[var(--flyout-top,0px)] ltr:tw-left-[calc(100%_+_8px)] rtl:tw-right-[calc(100%_+_8px)] tw-z-[200] tw-list-none tw-m-0 tw-bg-white tw-border-[0.5px] tw-border-gray-200 tw-rounded-[8px] tw-shadow-[0_4px_12px_rgba(0,0,0,0.08),0_2px_4px_rgba(0,0,0,0.04)] tw-min-w-[180px] tw-p-[4px] tw-flex-col tw-gap-[2px]',
                      'group-hover:tw-visible',
                    )}
                  >
                    {overflowItems.map((item) => {
                      const isActive = CheckIsActiveLink(item);
                      const itemPath =
                        item.link?.replace(
                          '{userId}',
                          user.userId?.toString() || '',
                        ) || '#';
                      return (
                        <li
                          key={item.link || item.label}
                          className="tw-list-none tw-m-0 tw-p-0 tw-relative tw-group/nav"
                          onMouseEnter={(e) => {
                            if (isMobile || !item.subItems) return;
                            setActiveLabel(item.label);
                            fitFlyoutToViewport(e.currentTarget);
                          }}
                          onMouseLeave={() =>
                            !isMobile &&
                            item.subItems &&
                            setActiveLabel(undefined)
                          }
                        >
                          <Link
                            href={itemPath}
                            className={clsx(
                              'tw-flex tw-items-center tw-gap-[8px] tw-h-[32px] tw-px-[8px] tw-rounded-[4px] tw-no-underline tw-text-[13px] tw-transition-colors tw-duration-100',
                              isActive
                                ? 'tw-bg-brand-50 tw-text-brand-500 tw-font-medium'
                                : 'tw-text-gray-700 hover:tw-bg-brand-50 hover:tw-text-brand-500',
                            )}
                            onClick={(e) => {
                              if (!item.link) {
                                e.preventDefault();
                                setActiveLabel(
                                  activeLabel === item.label
                                    ? undefined
                                    : item.label,
                                );
                              }
                            }}
                          >
                            {item.icon && (
                              <span className="tw-shrink-0 tw-inline-flex tw-w-[20px] tw-h-[20px] tw-items-center tw-justify-center tw-text-gray-500">
                                <RailIcon
                                  name={item.icon}
                                  size={18}
                                  strokeWidth={1.8}
                                />
                              </span>
                            )}
                            <span className="tw-truncate tw-flex-1">
                              {t(item.label)}
                            </span>
                          </Link>
                          {item.subItems &&
                            renderSubMenu(
                              item.subItems,
                              isActive || activeLabel === item.label,
                              item.label,
                              item.icon,
                            )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              </ul>
            )}
            {hasPermission('Settings.View') && (
              <li
                aria-hidden="true"
                className="tw-mt-auto tw-list-none tw-m-0 tw-p-0"
              >
                <hr className="tw-mx-[12px] tw-my-[4px] tw-border-0 tw-border-t-[0.5px] tw-border-gray-100" />
              </li>
            )}
            {renderNavItem(settingItem, true)}
          </ul>
        )}
      </div>
    </>
  );
};

export default Sidenav;
